import {ICON_TILE_COLORS, PhoneHQGlyph, PhoneHQIconAvatar, tileForColorKey} from 'ch3-ui-lib';
import {Check} from 'lucide-react';
import {useState} from 'react';

/** Material icon names a group or line can use (stored as is). */
export const TILE_ICONS = ['home', 'account_circle', 'shopping_cart', 'favorite', 'lock', 'thumb_up', 'lightbulb', 'star', 'account_balance_wallet', 'store', 'work', 'savings', 'pets', 'bookmark', 'leaderboard', 'pan_tool', 'rocket_launch', 'perm_media', 'shopping_bag', 'contact_support', 'notifications', 'school', 'cookie', 'sports_football', 'weekend', 'cloud', 'email', 'forum', 'chat_bubble', 'place', 'local_library', 'park', 'local_cafe', 'hotel', 'navigation', 'diamond', 'terrain', 'wine_bar', 'icecream', 'play_arrow', 'videocam', 'movie', 'spa', 'room_service'] as const;

const label = (icon: string) => icon.replace(/_/g, ' ');

interface IconPickerProps {
    currentIcon?: string | null;
    currentColor?: string | null;
    setIcon: (icon: string) => void;
    setColor: (color: string) => void;
}

/**
 * Icon and colour for a group or line (SettingsPhone-Avatar): a preview tile, the icon grid and the colours.
 * The chosen icon shows in the chosen colour; everything else stays neutral.
 */
export const IconPicker = ({currentIcon, currentColor, setIcon, setColor}: IconPickerProps) => {
    const [icon, setIconState] = useState(currentIcon ?? 'home');
    const [color, setColorState] = useState(currentColor ?? ICON_TILE_COLORS[0].key);
    const tile = tileForColorKey(color);
    const selectedSwatch = ICON_TILE_COLORS.find(c => tileForColorKey(c.key) === tile)?.key;

    const pickIcon = (next: string) => {
        setIconState(next);
        setIcon(next);
    };
    const pickColor = (next: string) => {
        setColorState(next);
        setColor(next);
    };

    return (
        <div className="flex w-[376px] max-w-full flex-col gap-4">
            <div className="flex items-center gap-3">
                <PhoneHQIconAvatar icon={icon} color={color} size="64px"/>
                <div className="flex flex-col gap-0.5">
                    <span className="text-callout font-semibold text-ink">Preview</span>
                    <span className="text-footnote text-ink-muted">How the tile looks in lists and menus.</span>
                </div>
            </div>

            <div className="flex flex-col gap-2">
                <span id="icon-picker-icons" className="text-footnote font-semibold text-ink-muted">Icon</span>
                <div role="radiogroup" aria-labelledby="icon-picker-icons" className="grid grid-cols-8 gap-1.5">
                    {TILE_ICONS.map(name => {
                        const selected = name === icon;
                        return (
                            <button
                                key={name}
                                type="button"
                                role="radio"
                                aria-checked={selected}
                                aria-label={label(name)}
                                title={label(name)}
                                phq-id="groupDetails-chooseIconBtn"
                                onClick={() => pickIcon(name)}
                                className={`flex h-9 cursor-pointer items-center justify-center rounded-control border-0 outline-none focus-visible:ring-2 focus-visible:ring-brand-focus ${
                                    selected
                                        ? `${ICON_TILE_COLORS.find(c => c.key === selectedSwatch)?.className ?? 'bg-tile-blue'} text-white`
                                        : 'bg-surface-muted text-ink-secondary hover:bg-line'
                                }`}
                            >
                                <PhoneHQGlyph name={name} size={20} className="!text-current"/>
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className="flex flex-col gap-2">
                <span id="icon-picker-colors" className="text-footnote font-semibold text-ink-muted">Background colour</span>
                <div role="radiogroup" aria-labelledby="icon-picker-colors" className="flex flex-wrap gap-2.5 p-1">
                    {ICON_TILE_COLORS.map(swatch => {
                        const selected = swatch.key === selectedSwatch;
                        return (
                            <button
                                key={swatch.key}
                                type="button"
                                role="radio"
                                aria-checked={selected}
                                aria-label={swatch.label}
                                title={swatch.label}
                                onClick={() => pickColor(swatch.key)}
                                className={`flex size-7 cursor-pointer items-center justify-center rounded-control border-0 text-white outline-none focus-visible:ring-2 focus-visible:ring-brand-focus focus-visible:ring-offset-2 focus-visible:ring-offset-surface ${swatch.className}`}
                                // Selected: a surface-coloured gap, then a ring in the swatch's own colour.
                                style={selected ? {boxShadow: `0 0 0 2px var(--surface), 0 0 0 4px var(--tile-${tileForColorKey(swatch.key)})`} : undefined}
                            >
                                {selected && <Check size={16} strokeWidth={3} aria-hidden="true" className="text-white"/>}
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};
